import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

type Horizontal = 'left' | 'center' | 'right' | number
type Vertical   = 'top'  | 'center' | 'bottom' | number

export interface AnchorOrigin {
  horizontal: Horizontal
  vertical: Vertical
}

interface PopoverProps {
  open: boolean
  anchorEl: HTMLElement | null
  anchorOrigin?: AnchorOrigin
  onClose?: () => void               // ← теперь будем вызывать
  disablePortal?: boolean
  children: React.ReactNode
  className?: string
}

const DEFAULT_ORIGIN: AnchorOrigin = {
  vertical:   'bottom',
  horizontal: 'left',
}

const Popover: React.FC<PopoverProps> = ({
  open,
  anchorEl,
  anchorOrigin = DEFAULT_ORIGIN,
  onClose,
  disablePortal,
  children,
  className
}) => {
  const [pos, setPos] = useState({ top: -9999, left: -9999 })
  const popoverRef = useRef<HTMLDivElement>(null)

  /* ---------- вычисляем позицию ----------------------------------------- */
  const calcPosition = () => {
    if (!anchorEl) return

    const rect = anchorEl.getBoundingClientRect()

    const vh = anchorOrigin.vertical
    const hh = anchorOrigin.horizontal

    const top = typeof vh === 'number'
      ? rect.top + vh
      : vh === 'top'
          ? rect.top
          : vh === 'center'
              ? rect.top + rect.height / 2
              : rect.bottom

    const left = typeof hh === 'number'
      ? rect.left + hh
      : hh === 'left'
          ? rect.left
          : hh === 'center'
              ? rect.left + rect.width / 2
              : rect.right

    setPos({
      top:  top  + window.scrollY,
      left: left + window.scrollX,
    })
  }

  /* ---------- позиция при открытии / ресайзе / скролле ------------------- */
  useLayoutEffect(() => {
    if (!open) return
    calcPosition()

    window.addEventListener('resize',  calcPosition)
    window.addEventListener('scroll',  calcPosition, true)

    return () => {
      window.removeEventListener('resize',  calcPosition)
      window.removeEventListener('scroll',  calcPosition, true)
    }
  }, [open, anchorEl, anchorOrigin])

  /* ---------- закрытие по клику снаружи и по Esc ------------------------- */
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.()
    }

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      const popoverContain = popoverRef.current?.contains(target)
      const anchorContain  = anchorEl?.contains(target)
      if (!popoverContain && !anchorContain) {
        onClose?.()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, anchorEl, onClose])

  /* ---------- если закрыт – ничего не рендерим --------------------------- */
  if (!open || !anchorEl) return null

  /* ---------- сам Popover ------------------------------------------------ */
  const node = (
    <div
      ref={popoverRef}
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        zIndex: 1300,
      }}
     className={`app-popover ${className ? className : ''}`}
    >
      {children}
    </div>
  )

  if (disablePortal) return node
  return createPortal(node, document.body)
}

export default Popover